import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, Mail, Send, AlertTriangle } from 'lucide-react';

export const MeetingEmailSettingsDialog = ({ open, onOpenChange, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [sendTime, setSendTime] = useState('16:00');
  const [recipientsRaw, setRecipientsRaw] = useState('');
  const [senderId, setSenderId] = useState('');
  const [lang, setLang] = useState('de');
  const [connections, setConnections] = useState([]);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [lastError, setLastError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.getMeetingEmailSettings(), api.msConnections()]);
      setEnabled(!!s.data.enabled);
      setSendTime(s.data.send_time || '16:00');
      setRecipientsRaw((s.data.recipients || []).join(', '));
      setSenderId(s.data.sender_user_id || '');
      setLang(s.data.lang || 'de');
      setLastSentAt(s.data.last_sent_at || null);
      setLastError(s.data.last_error || null);
      setConnections(c.data.connections || []);
    } catch { toast.error('Failed to load email settings'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const parseRecipients = () =>
    recipientsRaw.split(/[\n,;]+/).map(r => r.trim()).filter(r => r.includes('@'));

  const buildPayload = () => ({
    enabled, send_time: sendTime, recipients: parseRecipients(),
    sender_user_id: senderId || null, lang,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveMeetingEmailSettings(buildPayload());
      toast.success('Email settings saved');
      onSaved?.();
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      await api.saveMeetingEmailSettings(buildPayload());
      const res = await api.sendPreMeetingEmailNow();
      toast.success(`Agenda email sent to ${res.data.sent_to?.length || 0} recipient(s)`);
      onSaved?.();
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send'); }
    setSending(false);
  };

  const sender = connections.find(c => c.user_id === senderId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="meeting-email-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-400" /> Automated Pre-Meeting Email
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4 mt-1">
            <p className="text-xs text-muted-foreground">
              Sends tomorrow's meeting agenda Mon–Fri at the configured time (Europe/Berlin) via Microsoft — only when the agenda is not empty. The email is auto-named after the next workday (e.g. "Pre-Meeting Montag, 13.07.2026"). One hour before, all team members get a push reminder to add their final deals.
            </p>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-foreground font-medium">Enable daily auto-email</p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="meeting-email-enabled-switch" />
            </div>

            <div className="flex items-center gap-3">
              <div className="w-32">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Send time (CET)</p>
                <Input type="time" value={sendTime} onChange={e => setSendTime(e.target.value)}
                  className="bg-secondary border-border rounded-sm h-9 text-sm text-foreground"
                  data-testid="meeting-email-time-input" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Email language</p>
                <Select value={lang} onValueChange={setLang}>
                  <SelectTrigger className="bg-secondary border-border rounded-sm h-9 text-sm" data-testid="meeting-email-lang-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-sm">
                    <SelectItem value="de" className="text-xs">Deutsch</SelectItem>
                    <SelectItem value="en" className="text-xs">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Recipients (comma separated)</p>
              <textarea value={recipientsRaw} onChange={e => setRecipientsRaw(e.target.value)}
                placeholder="anna@olta-nachfolge.com, ben@olta-nachfolge.com"
                rows={2}
                className="w-full bg-secondary border border-border rounded-sm p-2.5 text-sm text-foreground resize-y focus:outline-none focus:border-blue-500"
                data-testid="meeting-email-recipients-input" />
            </div>

            <div>
              <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">Send from (connected Microsoft account)</p>
              {connections.length === 0 ? (
                <p className="text-xs text-amber-400">No Microsoft accounts connected — connect one under Settings first.</p>
              ) : (
                <Select value={senderId} onValueChange={setSenderId}>
                  <SelectTrigger className="bg-secondary border-border rounded-sm h-9 text-sm" data-testid="meeting-email-sender-select">
                    <SelectValue placeholder="Select sender account" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-sm">
                    {connections.map(c => (
                      <SelectItem key={c.user_id} value={c.user_id} className="text-xs">
                        {c.name} ({c.account}){c.mail_ready ? '' : ' — no mail permission'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {sender && !sender.mail_ready && (
                <div className="flex items-start gap-2 mt-2 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2"
                  data-testid="meeting-email-reconnect-warning">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-amber-300">
                    This account hasn't granted email permission yet. The user must reconnect Microsoft under
                    Settings → Microsoft To Do to grant "Send mail".
                  </p>
                </div>
              )}
            </div>

            {(lastSentAt || lastError) && (
              <div className="text-[11px] text-muted-foreground border-t border-border pt-2" data-testid="meeting-email-last-status">
                {lastSentAt && <p>Last sent: {new Date(lastSentAt).toLocaleString('de-DE')}</p>}
                {lastError && <p className="text-red-400">Last error: {lastError}</p>}
              </div>
            )}

            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
              <Button onClick={handleSendNow} disabled={sending || saving} variant="outline"
                data-testid="meeting-email-send-now-btn"
                className="border-blue-600/30 text-blue-400 hover:bg-blue-600/10 rounded-sm text-xs h-8 px-3">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send now
              </Button>
              <div className="flex items-center gap-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)}
                  className="text-muted-foreground rounded-sm text-xs h-8 px-3">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} data-testid="meeting-email-save-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
                  {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
